import { memo, useEffect, useRef, useState } from 'react';
import { Handle, Position, NodeToolbar } from '@xyflow/react';
import { formatCurrency, formatDateTime } from '@/common/utils/formatters';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { ImageWithPlaceholder } from '@/common/components/ImageWithPlaceholder';
import { BriefcaseBusinessIcon, PlusIcon } from 'lucide-react';
import ImageWithDisplayName from '@/common/components/ImageWithDisplayName';
import { useCompanyBranch } from '@/common/hooks/queries/useCompanyBranch';
import { useCompanyBranchFinancialSummary } from '../../hooks/useCompanyBranchFinancialSummary';
import BranchDataLoadingState from './BranchDataLoadingState';
import FinancialSummaryLoadingState from './FinancialSummaryLoadingState';
import FinancialSummary from './FinancialSummary';
import Button from '@/common/components/Button';
import QueryString from 'qs';
import { CompanyContractConsumptionControlledBy } from '@/features/company/types/contract.type';

interface CustomNodeProps {
  data: {
    companyId: string
    kind: string
    display_name: string
    id: string
    parent_id?: string
    parentRef: React.RefObject<HTMLDivElement>
    consumptionControlledBy?: CompanyContractConsumptionControlledBy
  }
}

function CustomNode({ data }: CustomNodeProps) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [enableRequest, setEnableRequest] = useState(false);
  const [tooltipPositionSetted, setTooltipPositionSetted] = useState(false);
  const [createBranchUrl, setCreateBranchUrl] = useState('');

  const requested = useRef(false);

  const { data: branchData, isPending: isBranchDataPending } = useCompanyBranch(data.id || '', enableRequest);

  const {
    data: financialSummary,
    isPending: isFinancialSummaryPending,
  } = useCompanyBranchFinancialSummary(data.id || '', enableRequest);

  const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom'>('top');
  const [tooltipStyle, setTooltipStyle] = useState<{ top?: string; bottom?: string; left: string }>({ top: '0px', bottom: '0px', left: '0px' });

  const nodeRef = useRef<HTMLDivElement>(null);
  const firstColumnRef = useRef<HTMLDivElement>(null);

  const [secondColumnHeight, setSecondColumnHeight] = useState(0);

  const tooltipWidth = 416;

  useEffect(() => {
    if (tooltipOpen && nodeRef.current && data.parentRef.current) {
      const nodeRect = nodeRef.current.getBoundingClientRect();
      const parentRect = data.parentRef.current.getBoundingClientRect();

      const spaceTop = nodeRect.top - parentRect.top;
      const spaceBottom = parentRect.bottom - nodeRect.bottom;

      if (spaceTop > spaceBottom || spaceTop > 245) {
        setTooltipPosition('top');
      } else {
        setTooltipPosition('bottom');
      }

      const spaceLeft = nodeRect.left - parentRect.left;
      const spaceRight = parentRect.right - nodeRect.right;

      let adjustedLeft = 0;

      if (spaceRight < tooltipWidth / 2) {
        adjustedLeft = -(tooltipWidth / 2 - spaceRight - nodeRect.width / 2);
      } else if (spaceLeft < tooltipWidth / 2) {
        adjustedLeft = Math.max(adjustedLeft, tooltipWidth / 2 - spaceLeft - nodeRect.width / 2);
      }

      setTooltipStyle({ left: `${adjustedLeft}px` });
      setTooltipPositionSetted(true);
    }
  }, [tooltipOpen]);

  useEffect(() => {
    if (firstColumnRef.current && tooltipOpen && secondColumnHeight === 0) {
      setSecondColumnHeight(firstColumnRef.current.getBoundingClientRect().height);
    }
  }, [tooltipOpen, isBranchDataPending, firstColumnRef.current]);

  useEffect(() => {
    if (isBranchDataPending) {
      return;
    }

    const branchOption = {
      parent: {
        label: data.display_name,
        value: data.id,
      },
    };

    const url = `/company-branches/create?${QueryString.stringify(branchOption)}`;

    setCreateBranchUrl(url);
  }, [isBranchDataPending]);

  return (
    <div
      ref={nodeRef}
      className='p-5 pl-0'
      onMouseOver={() => {
        if (!requested.current) {
          setEnableRequest(true);
          requested.current = true;
        }

        setTooltipOpen(true)
      }}
      onMouseLeave={() => setTooltipOpen(false)}
    >
      <div
        className={twMerge(
          'relative px-4 py-2 rounded-md cursor-pointer',
          tooltipOpen && 'bg-white-200',
        )}
      >
        <NodeToolbar
          isVisible={tooltipOpen}
          position={tooltipPosition as Position}
          style={tooltipStyle}
        >
          <div className={twMerge('-my-3 nowheel', !tooltipPositionSetted && 'hidden')}>
            <div
              className={`w-[${tooltipWidth}px] flex bg-white-200 backdrop-blur-lg z-50 rounded-md cursor-default overflow-hidden`}
            >
              {isBranchDataPending && <BranchDataLoadingState />}

              {!isBranchDataPending && (
                <div className="w-[216px] p-4 flex flex-col gap-2" ref={firstColumnRef}>
                  <ImageWithDisplayName
                    placeholderIcon={BriefcaseBusinessIcon}
                    imageUrl={branchData?.general_information?.company_logo?.url}
                    displayName={branchData?.display_name}
                    displayNameClassName="text-sm font-bold"
                  />

                  {data.kind === 'public_company' ? (
                    <div>
                      <p className='text-xs font-light italic'>
                        Cota de empenho: <span className='font-bold not-italic'>
                          {formatCurrency(Number(branchData?.granted_credit) || 0)}
                        </span>
                      </p>

                      <p className='text-xs font-light italic'>
                        Total empenhado: <span className='font-bold not-italic'>
                          {formatCurrency(Number(branchData?.government_allocation_requested_credit) || 0)}
                        </span>
                      </p>

                      <p className='text-xs font-light italic'>
                        Total distribuído: <span className='font-bold not-italic'>
                          {formatCurrency(Number(branchData?.total_government_allocation_distributed_to_branch) || 0)}
                        </span>
                      </p>

                      <p className='text-xs font-light italic text-yellow-400'>
                        Total consumido: <span className='font-bold not-italic'>
                          {formatCurrency(Number(branchData?.consumed_credit) || 0)}
                        </span>
                      </p>

                      <p className='text-xs font-light italic text-valid'>
                        Total disponível: <span className='font-bold not-italic'>
                          {formatCurrency(Number(branchData?.remaining_credit) || 0)}
                        </span>
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className='text-xs font-light italic'>
                        Valor garantido: <span className='font-bold not-italic'>
                          {formatCurrency(Number(branchData?.granted_credit) || 0)}
                        </span>
                      </p>

                      <p className='text-xs font-light italic text-yellow-400'>
                        Total consumido: <span className='font-bold not-italic'>
                          {formatCurrency(Number(branchData?.consumed_credit) || 0)}
                        </span>
                      </p>

                      <p className='text-xs font-light italic text-valid'>
                        Total disponível: <span className='font-bold not-italic'>
                          {formatCurrency(Number(branchData?.remaining_credit) || 0)}
                        </span>
                      </p>
                    </div>
                  )}

                  <div className='flex items-baseline gap-1'>
                    <p className='italic font-light text-sm'>
                      Equipamentos:
                    </p>

                    <Link to={`/equipments?branch%5Blabel%5D=${branchData?.display_name}&branch%5Bvalue%5D=${data.id}`}>
                      <p className='text-xs underline'>
                        {branchData?.equipments_count}
                      </p>
                    </Link>
                  </div>

                  <div className='flex items-baseline gap-1'>
                    <p className='italic font-light text-sm'>
                      Usuários:
                    </p>

                    <Link to={`/users?branch%5Blabel%5D=${branchData?.display_name}&branch%5Bvalue%5D=${data.id}`}>
                      <p className='text-xs underline'>
                        {branchData?.client_users_count}
                      </p>
                    </Link>
                  </div>

                  <p className='text-[9px] italic font-light'>
                    {`Cadastrado em ${formatDateTime(new Date(branchData?.created_at || 0))}`}
                  </p>
                </div>
              )}

              {secondColumnHeight > 0 && (
                <div className='w-[200px] p-2 bg-black-900'>
                  <div
                    className='flex flex-col p-2 gap-2 overflow-auto thin-scroll'
                    style={{ height: secondColumnHeight }}
                  >
                    {isFinancialSummaryPending && <FinancialSummaryLoadingState />}

                    {!isFinancialSummaryPending && (
                      <FinancialSummary
                        branchId={data.id}
                        financialSummary={financialSummary}
                        companyKind={data.kind}
                        consumptionControlledBy={data.consumptionControlledBy}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </NodeToolbar>

        <div className="relative flex items-center gap-x-2">
          <Link to={`/company-branches/${data.id}`} className="flex items-center p-2">
            <ImageWithPlaceholder
              className={twMerge('size-6', !data.parent_id && 'size-8')}
              iconClassName={twMerge('size-4', !data.parent_id && 'size-6')}
              placeholderIcon={BriefcaseBusinessIcon}
              url={branchData?.general_information?.company_logo?.url}
              alt={branchData?.general_information?.fantasy_name}
            />

            <div className="ml-2 flex-1 max-w-44">
              <div className={twMerge("text-lg font-bold break-words whitespace-normal", !data.parent_id && 'text-3xl')}>
                {data.display_name}
              </div>
            </div>
          </Link>

          {data.kind === 'private_company' && (
            <Link
              to={createBranchUrl}
              className={twMerge('hidden absolute -bottom-6 -right-8', tooltipOpen && 'flex')}
            >
              <Button
                variant="link-white"
                className="bg-success border-2 border-white-800 p-0.5 rounded-full"
                operationKlassToCheckPermission="Client::CompanyBranches::Create"
              >
                <PlusIcon className="size-6" strokeWidth={2} />
              </Button>
            </Link>
          )}
        </div>

        <Handle
          type="target"
          position={Position.Top}
          className='invisible'
        />

        <Handle
          type="source"
          position={Position.Bottom}
          className='invisible'
        />
      </div>
    </div>
  );
};

export default memo(CustomNode);
